import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getEvents } from "../../../api/kquake";
import { getKQuakeEarthquakeApiFilter } from "../../../contexts/EarthquakeFilterContext";

import { EventsTable } from "../../EventsTable";
import { LoadingScreen } from "../../LoadingScreen";

const PERIOD_7_DAYS_IN_MS = 7 * 86400000;
const PERIOD_30_DAYS_IN_MS = 30 * 86400000;

const defaultFilterState = {
  period: {
    type: "-7d",
    start: "",
    end: "",
  },
  magnitude: {
    type: "2.5+",
    min: "",
    max: "",
  },
  location: {
    type: "all", // 'all', 'boxRange' or 'circleRange'
    minLat: "",
    maxLat: "",
    minLon: "",
    maxLon: "",
    centralLat: "",
    centralLon: "",
    maxRadius: "", // unit: km
  },
};

export const Events = () => {
  const [filter, setFilter] = useState(defaultFilterState);

  const { t } = useTranslation();
  const today = useRef(new Date());

  const handleFilterChanged = (event) => {
    if (event.target && event.target.name) {
      const { type, name } = event.target;
      if (type === "radio") {
        const [category, value] = name.split(/\.(.+)/);
        if (filter[category].type !== value) {
          setFilter({
            ...filter,
            [category]: { ...filter[category], type: value },
          });
        }
      } else if (type === "text") {
        const [category, key] = name.split(/\.(.+)/);
        const value = event.target.value.trim();
        if (filter[category][key] !== value) {
          setFilter({
            ...filter,
            [category]: { ...filter[category], [key]: value },
          });
        }
      }
    }
  };

  const checkFormAndGetApiFilter = () => {
    const period = { ...filter.period };
    const magnitude = { ...filter.magnitude };
    const location = { ...filter.location };

    // Magnitude
    magnitude.enabled = true;
    if (magnitude.type !== "custom") {
      switch (magnitude.type) {
        case "2.5+":
          magnitude.min = "2.5";
          magnitude.max = "";
          break;
        case "3.0+":
          magnitude.min = "3.0";
          magnitude.max = "";
          break;
        case "4.0+":
          magnitude.min = "4.0";
          magnitude.max = "";
          break;
        default:
          // console.error(`Unexpected magnitude type: ${magnitude.type}`)
          throw new Error(`Unexpected magnitude type: ${magnitude.type}`);
      }
    }

    // Period
    period.enabled = true;
    period.timezone = "KST";
    if (period.type !== "custom") {
      switch (period.type) {
        case "-7d":
          period.start = new Date(Date.now() - PERIOD_7_DAYS_IN_MS)
            .toISOString()
            .slice(0, 10);
          period.end = "";
          break;
        case "-30d":
          period.start = new Date(Date.now() - PERIOD_30_DAYS_IN_MS)
            .toISOString()
            .slice(0, 10);
          period.end = "";
          break;
        case "this-year":
          period.start = `${today.current.getFullYear()}-01-01`;
          period.end = "";
          break;
        default:
          // console.error(`Unexpected period type: ${period.type}`)
          throw new Error(`Unexpected period type: ${period.type}`);
      }
    }

    // Location
    if (location.type === "all") {
      location.enabled = false;
    } else {
      location.enabled = true;
    }

    return getKQuakeEarthquakeApiFilter({ period, magnitude, location });
  };

  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const searchEvents = async () => {
    let apiFilter = {};
    try {
      apiFilter = checkFormAndGetApiFilter();
    } catch (error) {
      alert(t("form.message." + error.message));
      return;
    }
    apiFilter["with-additional-info"] = true;

    setEvents([]);
    setIsLoading(true);
    try {
      const responseData = await getEvents(apiFilter);
      setEvents(responseData.sort((e1, e2) => e2.origin.time - e1.origin.time));
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      const alertMessage =
        error.httpStatus === 204
          ? t("form.message.noEvents")
          : [
              t("form.message.failedToRetrieve"),
              `Error message: ${error.message}`,
              t("form.message.contactAdministrator"),
            ].join("\r\n");
      alert(alertMessage);
    }
  };

  return (
    <div className="container">
      <div className="sub-container">
        {/* <div className="alert alert-danger"><i className="ion ion-md-information-circle-outline"></i> 로그인이 필요한 서비스입니다. </div> */}
        {/* <RedAlertBar message={searchErrorMessage} /> */}
        <div style={{ fontSize: "15px", marginBottom: "5px" }}>
          &nbsp;&nbsp;<i className="ion ion-md-help-circle-outline"></i>
          {t("page.event-data.desc")}
        </div>
        <div className="overflow-auto">
          <table className="datatable mb20">
            <colgroup className="">
              <col style={{ width: "20%" }} />
              <col style={{ width: "80%" }} />
            </colgroup>
            <tbody>
              <tr>
                <th className="first">{t("w.magnitude")}</th>
                <td className="first clearfix">
                  <div className="left">
                    <label
                      className="datatable_select_radio"
                      htmlFor="events.magnitude.2.5+"
                    >
                      <input
                        type="radio"
                        id="events.magnitude.2.5+"
                        name="magnitude.2.5+"
                        className="with-gap"
                        checked={filter.magnitude.type === "2.5+"}
                        onChange={handleFilterChanged}
                      />
                      <span>2.5+ &nbsp;&nbsp;&nbsp;&nbsp;</span>
                    </label>
                    <label
                      className="datatable_select_radio"
                      htmlFor="events.magnitude.3.0+"
                    >
                      <input
                        type="radio"
                        id="events.magnitude.3.0+"
                        name="magnitude.3.0+"
                        className="with-gap"
                        checked={filter.magnitude.type === "3.0+"}
                        onChange={handleFilterChanged}
                      />
                      <span>3.0+ &nbsp;&nbsp;&nbsp;&nbsp;</span>
                    </label>
                    <label
                      className="datatable_select_radio"
                      htmlFor="events.magnitude.4.0+"
                    >
                      <input
                        type="radio"
                        id="events.magnitude.4.0+"
                        name="magnitude.4.0+"
                        className="with-gap"
                        checked={filter.magnitude.type === "4.0+"}
                        onChange={handleFilterChanged}
                      />
                      <span>4.0+ &nbsp;&nbsp;&nbsp;&nbsp;</span>
                    </label>
                  </div>
                  <div className="left">
                    <label
                      className="datatable_select_radio"
                      htmlFor="events.magnitude.custom"
                    >
                      <input
                        type="radio"
                        id="events.magnitude.custom"
                        name="magnitude.custom"
                        className="with-gap"
                        checked={filter.magnitude.type === "custom"}
                        onChange={handleFilterChanged}
                      />
                      <span>{t("form.customInput")}</span>
                    </label>
                    <div className="inline-block">
                      <input
                        type="text"
                        className="multiinput"
                        name="magnitude.min"
                        placeholder="2.5"
                        aria-label="이벤트 규모 최소값"
                        onBlur={handleFilterChanged}
                      />
                      ~
                      <input
                        type="text"
                        className="multiinput"
                        name="magnitude.max"
                        placeholder="6.0"
                        aria-label="이벤트 규모 최대값"
                        onBlur={handleFilterChanged}
                      />
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <th>{t("w.duration")}</th>
                <td className="clearfix">
                  <div className="md4">
                    <label
                      className="datatable_select_radio"
                      htmlFor="events.period.-7d"
                    >
                      <input
                        type="radio"
                        id="events.period.-7d"
                        name="period.-7d"
                        className="with-gap"
                        checked={filter.period.type === "-7d"}
                        onChange={handleFilterChanged}
                      />
                      <span>
                        {t("form.duration.last7days")}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </span>
                    </label>
                    <label
                      className="datatable_select_radio"
                      htmlFor="events.period.-30d"
                    >
                      <input
                        type="radio"
                        id="events.period.-30d"
                        name="period.-30d"
                        className="with-gap"
                        checked={filter.period.type === "-30d"}
                        onChange={handleFilterChanged}
                      />
                      <span>
                        {t("form.duration.last30days")}
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </span>
                    </label>
                    <label
                      className="datatable_select_radio"
                      htmlFor="events.period.this-year"
                    >
                      <input
                        type="radio"
                        id="events.period.this-year"
                        name="period.this-year"
                        className="with-gap"
                        checked={filter.period.type === "this-year"}
                        onChange={handleFilterChanged}
                      />
                      <span>
                        {t("form.duration.year", {
                          year: today.current.getFullYear(),
                        })}
                      </span>
                    </label>
                  </div>
                  <div className="left ">
                    <label
                      className="datatable_select_radio"
                      htmlFor="events.period.custom"
                    >
                      <input
                        type="radio"
                        id="events.period.custom"
                        name="period.custom"
                        className="with-gap"
                        checked={filter.period.type === "custom"}
                        onChange={handleFilterChanged}
                      />
                      <span className="block-mobile">
                        {t("form.customInput")}
                      </span>
                    </label>
                    <br className="show_mobile_480 hide" />
                    <input
                      type="text"
                      className="datepicker multiinput2"
                      name="period.start"
                      placeholder="2013-01-01"
                      aria-label="이벤트 기간 시작일"
                      onBlur={handleFilterChanged}
                    />
                    <div className="inline-block">
                      ~
                      <input
                        type="text"
                        className="datepicker multiinput2"
                        name="period.end"
                        placeholder={today.current.toISOString().slice(0, 10)}
                        aria-label="이벤트 기간 종료일"
                        onBlur={handleFilterChanged}
                      />
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <th>{t("w.location")}</th>
                <td>
                  <div className="mb4">
                    <label
                      className="datatable_select_radio"
                      htmlFor="events.location.all"
                    >
                      <input
                        type="radio"
                        id="events.location.all"
                        name="location.all"
                        className="with-gap"
                        checked={filter.location.type === "all"}
                        onChange={handleFilterChanged}
                      />
                      <span>{t("form.all")}</span>
                    </label>
                  </div>
                  <div className="left multiinput_container">
                    <label className="datatable_select_radio">
                      <input
                        type="radio"
                        name="location.boxRange"
                        className="with-gap"
                        aria-label="이벤트 위경도 범위 지정"
                        checked={filter.location.type === "boxRange"}
                        onChange={handleFilterChanged}
                      />
                      <span>
                        {t("w.latitude")}
                        <span className="hide-on-med-and-down">
                          &nbsp;{t("w.range")}
                        </span>
                      </span>
                    </label>
                    <input
                      type="text"
                      name="location.minLat"
                      placeholder="35.0"
                      className="multiinput"
                      aria-label="이벤트 최소 위도"
                      onBlur={handleFilterChanged}
                    />
                    &nbsp; ~ &nbsp;
                    <input
                      type="text"
                      name="location.maxLat"
                      placeholder="38.0"
                      className="multiinput"
                      aria-label="이벤트 최대 위도"
                      onBlur={handleFilterChanged}
                    />
                  </div>
                  <div className="left multiinput_container">
                    <label className="datatable_select_radio">
                      <input
                        type="radio"
                        className="with-gap"
                        style={{ cursor: "default" }}
                        tabIndex="-1"
                        disabled
                      />
                      <span className="be_disabled">
                        {t("w.longitude")}
                        <span className="hide-on-med-and-down">
                          &nbsp;{t("w.range")}
                        </span>
                      </span>
                    </label>
                    <input
                      type="text"
                      name="location.minLon"
                      placeholder="126.0"
                      className="multiinput"
                      aria-label="이벤트 최소 경도"
                      onBlur={handleFilterChanged}
                    />
                    &nbsp; ~ &nbsp;
                    <input
                      type="text"
                      name="location.maxLon"
                      placeholder="129.0"
                      className="multiinput"
                      aria-label="이벤트 최대 경도"
                      onBlur={handleFilterChanged}
                    />
                  </div>
                  <div className="clearfix"></div>
                  <div className="left multiinput_container">
                    <label className="datatable_select_radio">
                      <input
                        type="radio"
                        name="location.circleRange"
                        className="with-gap"
                        aria-label="이벤트 위경도 중심 및 범위 지정"
                        checked={filter.location.type === "circleRange"}
                        onChange={handleFilterChanged}
                      />
                      <span>
                        <span className="hide-on-med-and-down">
                          {t("form.location.latitudeAndLongitude")}
                        </span>
                        {t("form.location.center")}
                      </span>
                    </label>
                    <input
                      type="text"
                      name="location.centralLat"
                      placeholder="36.3"
                      className="multiinput"
                      aria-label="이벤트 중심 위도"
                      onBlur={handleFilterChanged}
                    />
                    , &nbsp;
                    <input
                      type="text"
                      name="location.centralLon"
                      placeholder="127.5"
                      className="multiinput"
                      aria-label="이벤트 중심 경도"
                      onBlur={handleFilterChanged}
                    />
                  </div>
                  <div className="left multiinput_container">
                    <label className="datatable_select_radio">
                      <input
                        type="radio"
                        className="with-gap"
                        style={{ cursor: "default" }}
                        tabIndex="-1"
                        disabled
                      />
                      <span className="be_disabled">
                        {t("form.location.radius")}
                      </span>
                    </label>
                    <input
                      type="text"
                      name="location.maxRadius"
                      placeholder="30"
                      className="multiinput"
                      aria-label="이벤트 반경"
                      onBlur={handleFilterChanged}
                    />
                    km
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="center-align row">
            <button
              type="button"
              className="waves-effect waves-light button-lg btn-block"
              onClick={searchEvents}
            >
              {t("w.search")}
            </button>
          </div>
        </div>
      </div>
      <EventsTable events={events} setIsLoading={setIsLoading} />
      <LoadingScreen screen={isLoading} />
    </div>
  );
};
